import { useEffect, useMemo, useRef, useState } from "react";
import { Play, Pause, RotateCcw, Maximize2, Plus, Minus } from "lucide-react";

const MODES = {
  fortime: { label: "For Time", help: "Counts up. Optional time cap." },
  amrap: { label: "AMRAP", help: "Counts down. Tap + for each round." },
  emom: { label: "EMOM", help: "A new round every interval." },
  tabata: { label: "Tabata", help: "20 s work, 10 s rest, 8 rounds." },
  intervals: { label: "Intervals", help: "Your own work and rest." },
};

let ctx, comp;
// 24 Oct 2026 (member feedback: "timer beep too quiet on iPhone"): louder, punchier beeps through a
// compressor at near full level, "playback" audio so the silent switch does not mute it, and the
// sound context woken on the first tap so iPhone allows it.
function audio() {
  if (!ctx) {
    ctx = new (window.AudioContext || window.webkitAudioContext)();
    try { if (navigator.audioSession) navigator.audioSession.type = "playback"; } catch (_) { /* older iOS */ }
    comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -6; comp.knee.value = 0; comp.ratio.value = 4; comp.attack.value = 0.002; comp.release.value = 0.1;
    comp.connect(ctx.destination);
  }
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  return ctx;
}
if (typeof window !== "undefined") {
  const wake = () => { try { audio(); } catch (_) { /* ignore */ } };
  window.addEventListener("touchstart", wake, { once: true, passive: true });
  window.addEventListener("pointerdown", wake, { once: true });
}
function beep(freq = 880, ms = 160, vol = 0.9) {
  try {
    const c = audio();
    const t = c.currentTime;
    [freq, freq * 2].forEach((f, i) => {            // a tone plus its octave: cuts through gym noise
      const o = c.createOscillator(), g = c.createGain();
      o.frequency.value = f; o.type = i ? "square" : "sawtooth";
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(i ? vol * 0.35 : vol, t + 0.005);
      g.gain.setValueAtTime(i ? vol * 0.35 : vol, t + ms / 1000 - 0.02);
      g.gain.linearRampToValueAtTime(0, t + ms / 1000);
      o.connect(g); g.connect(comp); o.start(t); o.stop(t + ms / 1000 + 0.01);
    });
  } catch (_) { /* audio not available */ }
}

const fmt = (s) => { s = Math.max(0, Math.round(s)); const m = Math.floor(s / 60); return `${String(m).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`; };

function Stepper({ label, value, set, min = 0, max = 99, unit = "" }) {
  return (
    <div className="rounded-xl border border-[#1E222A] bg-[#0B0D11] p-3">
      <p className="mono-label">{label}</p>
      <div className="flex items-center justify-between mt-1">
        <button onClick={() => set(Math.max(min, value - 1))} className="p-2 rounded-full border border-[#343A4A]" aria-label={`Less ${label}`}><Minus className="w-4 h-4" /></button>
        <span className="font-heading font-black text-2xl">{value}{unit}</span>
        <button onClick={() => set(Math.min(max, value + 1))} className="p-2 rounded-full border border-[#343A4A]" aria-label={`More ${label}`}><Plus className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

export default function Timer() {
  const [mode, setMode] = useState("fortime");
  const [cap, setCap] = useState(20);
  const [mins, setMins] = useState(12);
  const [rounds, setRounds] = useState(10);
  const [every, setEvery] = useState(60);
  const [work, setWork] = useState(40);
  const [rest, setRest] = useState(20);
  const [state, setState] = useState("idle"); // idle | countdown | running | paused | done
  const [t, setT] = useState(0);              // elapsed seconds since GO
  const [pre, setPre] = useState(10);
  const [laps, setLaps] = useState(0);
  const wake = useRef(null);
  const box = useRef(null);
  const last = useRef(null);

  // memoised so the ticking interval isn't torn down every 100 ms (that made interval modes run slow)
  const plan = useMemo(() => {
    if (mode === "tabata") return { work: 20, rest: 10, rounds: 8 };
    if (mode === "intervals") return { work, rest, rounds };
    if (mode === "emom") return { work: every, rest: 0, rounds };
    return null;
  }, [mode, work, rest, rounds, every]);
  const pausedFrom = useRef("running");
  const total = mode === "fortime" ? cap * 60 : mode === "amrap" ? mins * 60 : plan.rounds * (plan.work + plan.rest);

  useEffect(() => {
    if (state !== "countdown" && state !== "running") return undefined;
    last.current = performance.now();
    const id = setInterval(() => {
      const nowp = performance.now(); const dt = (nowp - last.current) / 1000; last.current = nowp;
      if (state === "countdown") {
        setPre((p) => {
          const np = p - dt;
          if (Math.ceil(np) !== Math.ceil(p) && np > 0 && np <= 3) beep(660);
          if (np <= 0) { beep(1200, 400); setState("running"); return 0; }
          return np;
        });
      } else {
        setT((x) => {
          const nx = x + dt;
          if (plan) {
            const cyc = plan.work + plan.rest;
            const before = x % cyc, after = nx % cyc;
            if (Math.floor(nx / cyc) !== Math.floor(x / cyc) && nx < total) beep(1200, 300);
            else if (plan.rest && before < plan.work && after >= plan.work) beep(500, 300);
            const remIn = (plan.rest && after < plan.work ? plan.work : cyc) - after;
            if (Math.ceil(remIn) !== Math.ceil(remIn + dt) && remIn <= 3 && remIn > 0) beep(660, 100);
          }
          if (total && nx >= total) { beep(1200, 900); setState("done"); return total; }
          return nx;
        });
      }
    }, 100);
    return () => clearInterval(id);
  }, [state, plan, total]);

  useEffect(() => {
    const want = state === "running" || state === "countdown";
    if (want && "wakeLock" in navigator && !wake.current) navigator.wakeLock.request("screen").then((w) => { wake.current = w; }).catch(() => {});
    if (!want && wake.current) { wake.current.release().catch(() => {}); wake.current = null; }
  }, [state]);

  const start = () => { if (state === "paused") setState(pausedFrom.current); else { setT(0); setLaps(0); setPre(10); setState("countdown"); beep(440, 80, 0.05); } };
  const reset = () => { setState("idle"); setT(0); setPre(10); setLaps(0); };
  const full = () => { try { box.current?.requestFullscreen?.(); } catch (_) {} };

  let big = fmt(mode === "fortime" ? t : total - t), sub = "", phase = "", color = "#CCFF00";
  if (plan && state !== "idle") {
    const cyc = plan.work + plan.rest; const r = Math.min(plan.rounds, Math.floor(t / cyc) + 1); const inC = t % cyc;
    const working = inC < plan.work;
    big = fmt(working ? plan.work - inC : cyc - inC);
    sub = `round ${r} of ${plan.rounds}`; phase = plan.rest ? (working ? "work" : "rest") : "";
    color = working ? "#CCFF00" : "#3D7BFF";
  }
  if (state === "countdown") { big = String(Math.ceil(pre)); sub = "get ready"; color = "#FFB340"; phase = ""; }
  if (state === "done") { sub = "time!"; color = "#FF5A5F"; }

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div className="flex flex-wrap gap-2" data-testid="timer-modes">
        {Object.entries(MODES).map(([k, m]) => (
          <button key={k} onClick={() => { setMode(k); reset(); }} className={`rounded-full px-4 h-10 text-sm border ${mode === k ? "bg-acid text-black border-acid" : "border-[#343A4A] text-[#B7BECB]"}`}>{m.label}</button>
        ))}
      </div>
      <div ref={box} className="relative overflow-hidden rounded-3xl border border-[#1E222A] bg-[#050505] min-h-[22rem] flex flex-col items-center justify-center p-6" data-testid="timer-face">
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{ background: `radial-gradient(circle at 50% 40%, ${color}33, transparent 60%)` }} />
        <p className="mono-label relative">{MODES[mode].label}{phase ? ` · ${phase}` : ""}</p>
        <p className="relative font-mono2 font-black leading-none tracking-tight" style={{ fontSize: "min(28vw, 11rem)", color }} data-testid="timer-digits">{big}</p>
        <p className="relative font-heading font-bold text-xl text-[#B7BECB] mt-2">{sub}{mode === "amrap" && state !== "idle" ? ` ${laps} rounds` : ""}</p>
        <div className="relative flex items-center gap-3 mt-6">
          {state === "running" || state === "countdown" ? (
            <button onClick={() => { pausedFrom.current = state; setState("paused"); }} className="w-16 h-16 rounded-full bg-white text-black flex items-center justify-center" aria-label="Pause"><Pause className="w-7 h-7" /></button>
          ) : (
            <button onClick={start} data-testid="timer-start" className="w-16 h-16 rounded-full bg-acid text-black flex items-center justify-center shadow-[0_0_40px_rgba(204,255,0,0.4)]" aria-label="Start"><Play className="w-7 h-7 ml-1" /></button>
          )}
          <button onClick={reset} className="w-12 h-12 rounded-full border border-[#343A4A] flex items-center justify-center" aria-label="Reset"><RotateCcw className="w-5 h-5" /></button>
          {mode === "amrap" && state === "running" && <button onClick={() => { setLaps(laps + 1); beep(990, 80); }} className="h-12 px-5 rounded-full border border-acid text-acid font-medium">+ round</button>}
          <button onClick={full} className="w-12 h-12 rounded-full border border-[#343A4A] flex items-center justify-center" aria-label="Full screen"><Maximize2 className="w-5 h-5" /></button>
        </div>
      </div>
      {state === "idle" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {mode === "fortime" && <Stepper label="time cap" value={cap} set={setCap} min={1} unit=" min" />}
          {mode === "amrap" && <Stepper label="minutes" value={mins} set={setMins} min={1} />}
          {mode === "emom" && <><Stepper label="rounds" value={rounds} set={setRounds} min={1} /><Stepper label="every (s)" value={every} set={setEvery} min={10} max={600} /></>}
          {mode === "intervals" && <><Stepper label="rounds" value={rounds} set={setRounds} min={1} /><Stepper label="work (s)" value={work} set={setWork} min={5} max={600} /><Stepper label="rest (s)" value={rest} set={setRest} min={0} max={600} /></>}
          <p className="col-span-2 md:col-span-4 text-sm text-[#8A91A6]">{MODES[mode].help} A 10-second countdown and beeps keep you on time; the screen stays awake while it runs.</p>
        </div>
      )}
    </div>
  );
}
